import ApplicationController from 'javascript/controllers/application_controller';
import { Rails } from 'javascript/utils/rails_polyfills';

export default class extends ApplicationController {
  static targets = [
    'contentContainer',
    'title',
    'titleText',
    'form',
    'saveButton',
    'loadingIndicator',
  ];

  bodyClassNameForModalIsShowing = 'modal-is-showing';

  get shouldShow() {
    return false || this.data.get('shouldShow') === 'true';
  }

  set shouldShow(value) {
    return this.data.set('shouldShow', value);
  }

  get allowCloseOnBackdropClick() {
    return this.data.get('allowCloseOnBackdropClick') === 'false'
      ? false
      : true;
  }

  set allowCloseOnBackdropClick(value) {
    return this.data.set('allowCloseOnBackdropClick', value);
  }

  get contentSource() {
    return '' || this.data.get('contentSource');
  }

  resetAttributesToDefaults() {
    this.unsetLoading();
    // this.titleTarget = '';
    this.data.set('fullscreen', false);
  }

  updateBodyDOMBasedOnCurrentState() {
    const bodyElement = this.document.body;
    const scrollYAttributeName = 'data-modal-last-scroll-y';

    if (this.shouldShow) {
      if (this.window.scrollY !== 0) {
        bodyElement.setAttribute(scrollYAttributeName, this.window.scrollY);
      }
      bodyElement.classList.add(this.bodyClassNameForModalIsShowing);
    } else {
      bodyElement.classList.remove(this.bodyClassNameForModalIsShowing);
      this.window.setTimeout(() => {
        const scrollY = bodyElement.getAttribute(scrollYAttributeName);
        if (scrollY) {
          this.document.documentElement.scrollTop = bodyElement.scrollTop =
            scrollY;
        }
        bodyElement.setAttribute(scrollYAttributeName, '');
      }, 0);
    }
  }

  setLoading() {
    this.element.classList.add('contents-loading');
  }

  unsetLoading() {
    this.element.classList.remove('contents-loading');
  }

  show() {
    this.shouldShow = true;
  }

  close = () => {
    this.shouldShow = false;
    this.allowCloseOnBackdropClick = true;
    this.clearQueryParams();

    setTimeout(() => {
      this.removeContent();
    }, 200);
  };

  removeContent() {
    this.contentContainerTarget.className = 'modal__content';

    while (this.contentContainerTarget.firstChild) {
      this.contentContainerTarget.removeChild(
        this.contentContainerTarget.firstChild,
      );
    }
  }

  updateContent() {
    const template = this.document.querySelector(this.contentSource);

    if (template) {
      const templateAllowCloseOnBackdropClick = template.getAttribute(
        'data-modal-allow-close-on-backdrop-click',
      );

      if (templateAllowCloseOnBackdropClick) {
        this.allowCloseOnBackdropClick =
          templateAllowCloseOnBackdropClick === 'false' ? false : true;
      }

      this.contentContainerTarget.innerHTML = template.innerHTML;
      this.contentContainerTarget.className =
        'modal__content ' + template.className;
      this.scrollToTop();

      this.show();
    }
  }

  onBackdropClick() {
    if (this.allowCloseOnBackdropClick) {
      this.close();
    }
  }

  onFormSubmit() {
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = true;
      this.saveButtonTarget.classList.add('button--loading');
    }
  }

  onSubmitButtonClick() {
    this.onFormSubmit();

    console.log('hasFormTarget:', this.hasFormTarget);

    if (this.hasFormTarget) {
      console.log('formTarget:', this.formTarget);
      Rails.fire(this.formTarget, 'submit');
    }
  }

  onShouldShowChanged() {
    this.updateBodyDOMBasedOnCurrentState();

    if (this.shouldShow) {
      if (this.hasTitleTextTarget) {
        this.titleTextTarget.innerHTML = this.data.get('title');
      }
      if (this.data.get('fullscreen') === 'true') {
        this.allowCloseOnBackdropClick = false;
      }
    } else {
      this.window.setTimeout(() => {
        this.resetAttributesToDefaults();
      }, 200);
    }
  }

  onCloseButtonClick = () => {
    this.close();
  };

  scrollToTop() {
    this.contentContainerTarget.scrollTop = 0;
  }

  handleLoading = ({ detail }) => {
    this.loadingIndicatorTarget.classList.toggle(
      'is-loading',
      detail.isLoading,
    );
  };

  setTitleContent(container, title) {
    if (
      typeof title === 'string' &&
      (title.includes('<') || title.includes('&lt;'))
    ) {
      const decoded = title.includes('&lt;')
        ? title
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, '&')
        : title;
      container.innerHTML = decoded;
    } else if (typeof title === 'string') {
      container.textContent = title;
    } else {
      container.append(title);
    }
  }

  setModalTitle = ({ detail }) => {
    const titleContainer = document.createElement('div');
    this.setTitleContent(titleContainer, detail.title);

    while (this.titleTarget.firstChild)
      this.titleTarget.removeChild(this.titleTarget.firstChild);

    if (this.titleTarget) this.titleTarget.appendChild(titleContainer);
  };

  connect() {
    const params = new URLSearchParams(window.location.search);
    if (params.has('open_modal')) this.toggleModalFromParams(params);

    if (this.hasLoadingIndicatorTarget && this.hasTitleTarget) {
      window.addEventListener('is-loading', this.handleLoading);
      window.addEventListener('set-modal-title', this.setModalTitle);
    }

    this.updateBodyDOMBasedOnCurrentState();

    this.attributeObserver = new MutationObserver((mutationList) => {
      if (
        mutationList.find(
          (x) => x.attributeName === 'data-modal-content-source',
        )
      ) {
        this.updateContent();
      }

      if (
        mutationList.find((x) => x.attributeName === 'data-modal-should-show')
      ) {
        this.onShouldShowChanged();
      }
    });

    this.attributeObserver.observe(this.element, { attributes: true });
    this.window.addEventListener('orientationchange', this.close);
    this.document.addEventListener('turbo:before-cache', this.close);
  }

  disconnect() {
    window.removeEventListener('is-loading', this.handleLoading);
    window.removeEventListener('set-modal-title', this.setModalTitle);
    this.attributeObserver.disconnect();
    this.window.removeEventListener('orientationchange', this.close);
    this.document.removeEventListener('turbo:before-cache', this.close);
  }

  toggleModalFromParams(params) {
    requestAnimationFrame(async () => {
      try {
        const contentSource = params.get('content_source');
        const title = params.get('open_modal');

        if (!contentSource) throw new Error('No modal content source');
        if (!title) throw new Error('No modal title');

        if (
          this.getMetaValue('howler:user.email') &&
          (title.toLowerCase() === 'login' || title.toLowerCase() === 'signup')
        ) {
          this.clearQueryParams();
          return;
        }

        this.element.setAttribute('data-modal-should-show', 'true');

        dispatchEvent(
          new CustomEvent('set-modal-title', {
            detail: { title: title || '' },
          }),
        );
        dispatchEvent(
          new CustomEvent('is-loading', { detail: { isLoading: true } }),
        );

        const res = await fetch(`${window.location.origin}/${contentSource}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'text/html',
            'X-CSRF-Token': this.getMetaValue('csrf-token'),
          },
        });

        const html = await res.text();
        this.contentContainerTarget.innerHTML = html;
      } catch (err) {
        console.error(err);
      }

      dispatchEvent(
        new CustomEvent('is-loading', { detail: { isLoading: false } }),
      );
    });
  }

  getMetaValue(name) {
    const element = document.head.querySelector(`meta[name="${name}"]`);
    return element && element.content;
  }

  clearQueryParams() {
    const url = new URL(window.location.href);
    url.searchParams.delete('open_modal');
    url.searchParams.delete('content_source');
    // The below line break Turbo's back functionality :-(
    window.history.replaceState(window.history.state, '', url);
  }
}
